import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useNavigate } from "react-router-dom";
import logoIcon from "@/assets/logo-icon.jpg";
import { Menu } from "lucide-react";
import WaitlistModal from "./WaitlistModal";

const UnauthenticatedNav = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);

  const navItems = [
    { label: "About", path: "/about" },
    { label: "Pricing", path: "/pricing" },
    { label: "Feedback", path: "/feedback" },
  ];

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  return (
    <>
      <nav className="flex items-center justify-between w-full">
        {/* Logo */}
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <img 
            src={logoIcon} 
            alt="Airing Down Logo" 
            className="w-10 h-10 rounded-lg"
          />
          <div>
            <h1 className="text-xl font-bold text-foreground">Airing Down</h1>
          <p className="text-xs text-muted-foreground">Off-Road Adventures, Mapped</p>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-1">
          {navItems.map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className="relative px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.label}
            </button>
          ))}
          
          <button
            onClick={() => navigate('/auth?early=true')}
            className="relative px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            Early Adopter Sign In
          </button>
          
          <Button variant="hero" size="sm" className="ml-4" onClick={() => setWaitlistModalOpen(true)}>
            Join Waitlist
          </Button>
        </div>

        {/* Mobile Navigation */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="md:hidden">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px]">
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-3">
                  <img 
                    src={logoIcon} 
                    alt="Airing Down Logo" 
                    className="w-8 h-8 rounded-lg"
                  />
                  <div>
                    <h2 className="text-lg font-bold text-foreground">Airing Down</h2>
                    <p className="text-xs text-muted-foreground">Off-Road Adventures, Mapped</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col space-y-4 flex-1">
                <Button
                  variant="ghost"
                  onClick={() => handleNavigation('/')}
                  className="justify-start text-left"
                >
                  Home
                </Button>
                
                {navItems.map((item) => (
                  <Button
                    key={item.path}
                    variant="ghost"
                    onClick={() => handleNavigation(item.path)}
                    className="justify-start text-left"
                  >
                    {item.label}
                  </Button>
                ))}
              </div>
              
              <div className="mt-8 space-y-2">
                <Button 
                  variant="hero" 
                  onClick={() => {
                    setIsOpen(false);
                    setWaitlistModalOpen(true);
                  }}
                  className="w-full"
                >
                  Join Waitlist
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setIsOpen(false);
                    navigate('/auth?early=true');
                  }}
                  className="w-full"
                >
                  Early Adopter Sign In
                </Button>
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </nav>

      {/* Waitlist Modal */}
      <WaitlistModal 
        open={waitlistModalOpen} 
        onOpenChange={setWaitlistModalOpen} 
      />
    </>
  );
};

export default UnauthenticatedNav;
